import { useEffect, useMemo, useState } from "react";
import { FiSave, FiClipboard, FiTrash } from "react-icons/fi";
import { Input, Modal } from "@iq/models";
import useAuthStore from "../../../states/authStore";
import {
	createTaskComment,
	deleteTask,
	deleteTaskComment,
	getProjectById,
	getTaskById,
	getTeamMembers,
	listTaskComments,
	TASK_PRIORITIES,
	TASK_STATUSES,
	updateTask,
} from "../../Services/pm_service";
import "../../../CRM/components/modals/Modal.css";
import "../../Styles/ViewTaskModal.css";

function toStatusLabel(status) {
	return String(status || "")
		.split("_")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

function toPriorityLabel(priority) {
	const normalized = String(priority || "").toLowerCase();
	if (normalized === "urgent") {
		return "Urgent";
	}
	if (normalized === "high") {
		return "High";
	}
	if (normalized === "medium") {
		return "Medium";
	}
	if (normalized === "low") {
		return "Low";
	}
	return "Medium";
}

function toDateInputValue(value) {
	if (!value) {
		return "";
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return date.toISOString().split("T")[0];
}

function formatTimestamp(value) {
	if (!value) {
		return "";
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return date.toLocaleString();
}

function sortCommentsDesc(items) {
	if (!Array.isArray(items)) {
		return [];
	}

	return [...items].sort((a, b) => {
		const aTime = new Date(a?.created_at || a?.createdAt || 0).getTime();
		const bTime = new Date(b?.created_at || b?.createdAt || 0).getTime();

		if (aTime !== bTime) {
			return bTime - aTime;
		}

		return Number(b?.id || 0) - Number(a?.id || 0);
	});
}

function getTaskDescription(taskRecord) {
	return (
		taskRecord?.description ??
		taskRecord?.task_description ??
		taskRecord?.taskDescription ??
		taskRecord?.details ??
		""
	);
}

function getTaskAssignedUserId(taskRecord) {
	return (
		taskRecord?.assigned_user_id ??
		taskRecord?.assignedUserId ??
		taskRecord?.assigned_user?.id ??
		null
	);
}

function ViewTaskModal({ isOpen, onClose, task, projectId, onTaskUpdated, onTaskDeleted }) {
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		assignedUserId: null,
		status: "not_started",
		priority: "medium",
		dueDate: "",
	});
	const [selectedAssigneeLabel, setSelectedAssigneeLabel] = useState("");
	const [selectedStatusLabel, setSelectedStatusLabel] = useState("");
	const [selectedPriorityLabel, setSelectedPriorityLabel] = useState("");
	const [teamMembers, setTeamMembers] = useState([]);
	const [projectOwnerUserId, setProjectOwnerUserId] = useState(null);
	const [comments, setComments] = useState([]);
	const [newComment, setNewComment] = useState("");
	const [contextLoading, setContextLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deletingTask, setDeletingTask] = useState(false);
	const [commentSubmitting, setCommentSubmitting] = useState(false);
	const [deletingCommentId, setDeletingCommentId] = useState(null);
	const [error, setError] = useState("");

	const currentUserId = useAuthStore((state) => state.userId);
	const taskId = task?.id;

	const canSave = useMemo(() => {
		return Boolean(formData.title.trim()) && !saving && !deletingTask;
	}, [deletingTask, formData.title, saving]);

	const canManageComments = useMemo(() => {
		return Number(currentUserId) > 0 && Number(currentUserId) === Number(projectOwnerUserId);
	}, [currentUserId, projectOwnerUserId]);

	const sortedComments = useMemo(() => sortCommentsDesc(comments), [comments]);

	const actionButtons = [
		{
			key: "delete-task",
			text: deletingTask ? "Deleting..." : "Delete Task",
			icon: <FiTrash />,
			type: "button",
			onClick: () => handleDeleteTask(),
			disabled: contextLoading || saving || deletingTask,
			color: "danger",
		},
		{
			key: "save-task",
			text: saving ? "Saving..." : "Save Changes",
			icon: <FiSave />,
			type: "button",
			onClick: () => handleSave(),
			disabled: !canSave || contextLoading || deletingTask,
			color: "accent",
		},
	];

	useEffect(() => {
		if (!isOpen || !taskId) {
			return;
		}

		let cancelled = false;

		async function loadContext() {
			setContextLoading(true);
			setError("");
			try {
				const [members, project, taskDetails, taskComments] = await Promise.all([
					getTeamMembers(),
					projectId ? getProjectById(projectId) : Promise.resolve(null),
					getTaskById(taskId),
					listTaskComments(taskId),
				]);

				if (cancelled) {
					return;
				}

				const projectMembers = Array.isArray(project?.members) ? project.members : [];
				const allowedUserIds = new Set(projectMembers.map((member) => Number(member?.user_id || member?.user?.id)));
				const filteredMembers = (Array.isArray(members) ? members : []).filter((member) =>
					allowedUserIds.has(Number(member?.id))
				);

				setTeamMembers(filteredMembers);
				setProjectOwnerUserId(Number(project?.owner_user_id || project?.ownerUserId || project?.owner_user?.id || 0));
				setComments(Array.isArray(taskComments) ? taskComments : []);

				const activeTask = taskDetails ?? task ?? {};
				const assignedUserId = getTaskAssignedUserId(activeTask);
				const assignee = filteredMembers.find((member) => Number(member.id) === Number(assignedUserId));

				setFormData({
					title: activeTask?.title ?? "",
					description: getTaskDescription(activeTask),
					assignedUserId: assignedUserId || null,
					status: activeTask?.status ?? "not_started",
					priority: activeTask?.priority ?? "medium",
					dueDate: toDateInputValue(activeTask?.due_date ?? activeTask?.dueDate),
				});
				setSelectedAssigneeLabel(assignee?.name || "");
				setSelectedStatusLabel(toStatusLabel(activeTask?.status ?? "not_started"));
				setSelectedPriorityLabel(toPriorityLabel(activeTask?.priority ?? "medium"));
				setNewComment("");
				setDeletingCommentId(null);
			} catch (contextError) {
				if (!cancelled) {
					setError(contextError?.message || "Failed to load task details");
				}
			} finally {
				if (!cancelled) {
					setContextLoading(false);
				}
			}
		}

		loadContext();

		return () => {
			cancelled = true;
		};
	}, [isOpen, projectId, task, taskId]);

	if (!isOpen || !taskId) {
		return null;
	}

	function handleFieldChange(event) {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		setError("");
	}

	async function handleSave() {
		if (!taskId) {
			return;
		}

		const title = formData.title.trim();
		if (!title) {
			setError("Task title is required");
			return;
		}

		try {
			setSaving(true);
			const updatedTask = await updateTask(taskId, {
				title,
				description: formData.description.trim(),
				assignedUserId: formData.assignedUserId ?? null,
				status: formData.status,
				priority: formData.priority,
				dueDate: formData.dueDate || null,
			});
			onTaskUpdated?.(updatedTask);
		} catch (saveError) {
			setError(saveError?.message || "Failed to save task changes");
		} finally {
			setSaving(false);
		}
	}

	async function handleCreateComment(event) {
		event.preventDefault();
		const content = newComment.trim();
		if (!content) {
			return;
		}

		try {
			setCommentSubmitting(true);
			const createdComment = await createTaskComment(taskId, content);
			setComments((prev) => [createdComment, ...prev]);
			setNewComment("");
		} catch (commentError) {
			setError(commentError?.message || "Failed to create comment");
		} finally {
			setCommentSubmitting(false);
		}
	}

	async function handleDeleteComment(commentId) {
		if (!canManageComments || !commentId) {
			return;
		}

		try {
			setDeletingCommentId(commentId);
			await deleteTaskComment(commentId);
			setComments((prev) => prev.filter((comment) => Number(comment?.id) !== Number(commentId)));
		} catch (deleteError) {
			setError(deleteError?.message || "Failed to delete comment");
		} finally {
			setDeletingCommentId(null);
		}
	}

	async function handleDeleteTask() {
		if (!taskId || deletingTask || saving) {
			return;
		}

		const taskTitle = (formData.title || task?.title || `Task #${taskId}`).trim();
		const confirmed = window.confirm(
			`Delete "${taskTitle}"? This action cannot be undone.`
		);
		if (!confirmed) {
			return;
		}

		try {
			setDeletingTask(true);
			setError("");
			await deleteTask(taskId);
			onTaskDeleted?.(taskId);
			onClose?.();
		} catch (deleteError) {
			setError(deleteError?.message || "Failed to delete task");
		} finally {
			setDeletingTask(false);
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={formData.title || "View Task"}
			icon={<FiClipboard />}
			actionButtons={actionButtons}
			errorMessage={error}
			tall
		>
			<div className="pm-task-view-layout">
				<div className="pm-task-view-left">
					<div className="modal-form-field">
						<label htmlFor="pm-view-task-title">Task Title</label>
						<Input
							id="pm-view-task-title"
							type="text"
							name="title"
							value={formData.title}
							onChange={handleFieldChange}
							disabled={saving || deletingTask || contextLoading}
							colorScheme="light"
						/>
					</div>

					<div className="modal-form-field">
						<label>Assignee</label>
						<Input
							type="dropdown"
							id="pm-view-task-assignee"
							name="pm-view-task-assignee"
							placeholder="Unassigned"
							value={selectedAssigneeLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Task assignee"
							renderInPortal={false}
							disabled={saving || deletingTask || contextLoading}
						>
							{({ closeMenu }) => (
								<>
									<button
										type="button"
										className="dropdown-menu-item"
										onClick={() => {
											setFormData((prev) => ({ ...prev, assignedUserId: null }));
											setSelectedAssigneeLabel("");
											closeMenu();
										}}
									>
										Unassigned
									</button>
									{teamMembers.map((member) => (
										<button
											key={member.id}
											type="button"
											className="dropdown-menu-item"
											onClick={() => {
												setFormData((prev) => ({ ...prev, assignedUserId: member.id }));
												setSelectedAssigneeLabel(member.name || member.email || "");
												closeMenu();
											}}
										>
											{member.name} ({member.email})
										</button>
									))}
									{teamMembers.length === 0 ? (
										<div className="dropdown-menu-empty">No project members available</div>
									) : null}
								</>
							)}
						</Input>
					</div>

					<div className="modal-form-field">
						<label>Status</label>
						<Input
							type="dropdown"
							id="pm-view-task-status"
							name="pm-view-task-status"
							placeholder="Status"
							value={selectedStatusLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Task status"
							renderInPortal={false}
							disabled={saving || deletingTask || contextLoading}
							options={TASK_STATUSES.map((status) => ({ value: status, label: toStatusLabel(status) }))}
							onOptionSelect={(option) => {
								setFormData((prev) => ({ ...prev, status: option.value }));
								setSelectedStatusLabel(option.label);
							}}
						/>
					</div>

					<div className="modal-form-field">
						<label>Priority</label>
						<Input
							type="dropdown"
							id="pm-view-task-priority"
							name="pm-view-task-priority"
							placeholder="Priority"
							value={selectedPriorityLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Task priority"
							renderInPortal={false}
							disabled={saving || deletingTask || contextLoading}
							options={TASK_PRIORITIES.map((priority) => ({ value: priority, label: toPriorityLabel(priority) }))}
							onOptionSelect={(option) => {
								setFormData((prev) => ({ ...prev, priority: option.value }));
								setSelectedPriorityLabel(option.label);
							}}
						/>
					</div>

					<div className="modal-form-field">
						<label htmlFor="pm-view-task-due-date">Due Date</label>
						<Input
							id="pm-view-task-due-date"
							type="date"
							name="dueDate"
							value={formData.dueDate}
							onChange={handleFieldChange}
							disabled={saving || deletingTask || contextLoading}
							colorScheme="light"
						/>
					</div>

					<div className="modal-form-field view-task-field">
						<label htmlFor="pm-view-task-description">Description</label>
						<Input
							id="pm-view-task-description"
							type="textarea"
							name="description"
							value={formData.description}
							onChange={handleFieldChange}
							rows={5}
							disabled={saving || deletingTask || contextLoading}
							colorScheme="light"
						/>
					</div>
				</div>
                <div className="pm-task-view-divider"/>
				<div className="pm-task-view-right">
					<h3 className="pm-task-comments-header">Comments</h3>
					<form className="pm-task-comment-form" onSubmit={handleCreateComment}>
						<textarea
							value={newComment}
							onChange={(event) => setNewComment(event.target.value)}
							placeholder="Write a comment..."
							disabled={commentSubmitting || deletingTask || contextLoading}
						/>
						<button
							type="submit"
							className="pm-task-comment-submit"
							disabled={commentSubmitting || deletingTask || !newComment.trim()}
						>
							{commentSubmitting ? "Posting..." : "Post Comment"}
						</button>
					</form>
					<div className="pm-task-comments-list">
						{sortedComments.length === 0 ? (
							<div className="pm-task-comments-empty">No comments yet.</div>
						) : (
							sortedComments.map((comment) => (
								<div key={comment.id} className="pm-task-comment-item">
									<div className="pm-task-comment-meta">
										<span className="pm-task-comment-author">
											{comment?.user?.name || comment?.user_name || "Unknown user"}
										</span>
										<span>{formatTimestamp(comment?.created_at || comment?.createdAt)}</span>
									</div>
									<div className="pm-task-comment-content">{comment?.content || ""}</div>
									{canManageComments ? (
										<button
											type="button"
											className="pm-task-comment-delete"
											onClick={() => handleDeleteComment(comment.id)}
											disabled={deletingTask || deletingCommentId === comment.id}
											aria-label="Delete comment"
											title="Delete comment"
										>
											<FiTrash />
										</button>
									) : null}
								</div>
							))
						)}
					</div>


				</div>
			</div>
		</Modal>
	);
}

export default ViewTaskModal;
